/**
 * Create Batch Sandbox page component.
 * Allows creating multiple sandboxes from the same disk image or snapshot.
 *
 * @module pages/CreateBatchSandboxPage
 */

import { useState, useRef, useMemo } from 'react';
import { useNavigate, Link } from 'react-router';
import { ArrowLeft, Save, X, Plus } from 'lucide-react';
import { PageHeader } from '@/components/layout';
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Button,
  Input,
  Select,
  LabelsEditor,
} from '@/components/ui';
import type { LabelsEditorRef } from '@/components/ui';
import type { BatchSandboxResponse } from '@/types';
import {
  useCreateBatchSandbox,
  useDiskImages,
  usePublicDiskImages,
  useAllSnapshots,
} from '@/hooks';
import { groupPublicDiskImages, groupsToSelectOptions } from '@/utils';
import type { SandboxRequest, BatchSandboxRequest } from '@/types';

type SourceType = 'diskImage' | 'publicDiskImage' | 'snapshot' | 'pod';
type VmmType = 'CloudHypervisor';

interface PodContainerEntry {
  name: string;
  diskImageType: 'public' | 'private';
  diskImageId: string;
  publicDiskImageId: string;
}

interface FormState {
  sourceType: SourceType;
  vmmType: VmmType;
  labels: Record<string, string>;
  diskImageId: string;
  publicDiskImageId: string;
  snapshotId: string;
  cpu: string;
  memory: string;
  disk: string;
  count: number;
  podContainers: PodContainerEntry[];
}

interface FormErrors {
  sourceType?: string;
  vmmType?: string;
  labels?: string;
  diskImageId?: string;
  publicDiskImageId?: string;
  snapshotId?: string;
  cpu?: string;
  memory?: string;
  disk?: string;
  count?: string;
  podContainers?: string;
}

const initialFormState: FormState = {
  sourceType: 'diskImage',
  vmmType: 'CloudHypervisor',
  labels: {},
  diskImageId: '',
  publicDiskImageId: '',
  snapshotId: '',
  cpu: '1000m',
  memory: '2048Mi',
  disk: '10Gi',
  count: 5,
  podContainers: [{ name: '', diskImageType: 'public', diskImageId: '', publicDiskImageId: '' }],
};

/**
 * Create batch sandbox page
 */
export function CreateBatchSandboxPage() {
  const navigate = useNavigate();
  const createBatchMutation = useCreateBatchSandbox();
  const { data: privateDiskImages, isLoading: diskImagesLoading } = useDiskImages();
  const { data: publicDiskImages, isLoading: publicDiskImagesLoading } = usePublicDiskImages();
  const { data: snapshotsData, isLoading: snapshotsLoading } = useAllSnapshots();
  const [form, setForm] = useState<FormState>(initialFormState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showResults, setShowResults] = useState(false);
  const [batchResults, setBatchResults] = useState<BatchSandboxResponse | null>(null);
  const [timeTakenMs, setTimeTakenMs] = useState<number>(0);
  const [selectedPublicImageGroup, setSelectedPublicImageGroup] = useState<string>('');

  // Ref for labels editor to get uncommitted draft
  const labelsEditorRef = useRef<LabelsEditorRef>(null);

  const handleChange = (field: keyof FormState, value: string | number) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (form.sourceType === 'diskImage' && !form.diskImageId) {
      newErrors.diskImageId = 'Please select a disk image';
    }

    if (form.sourceType === 'publicDiskImage' && !form.publicDiskImageId) {
      newErrors.publicDiskImageId = 'Please select a public disk image';
    }

    if (form.sourceType === 'snapshot' && !form.snapshotId) {
      newErrors.snapshotId = 'Please select a snapshot';
    }

    if (form.sourceType === 'pod') {
      for (const c of form.podContainers) {
        if (!c.name.trim()) {
          alert('Each container must have a name.');
          return false;
        }
        const hasImage = c.diskImageType === 'public' ? !!c.publicDiskImageId : !!c.diskImageId;
        if (!hasImage) {
          alert(`Container "${c.name}" must have a disk image.`);
          return false;
        }
      }
    }

    // Only validate CPU/memory when not creating from a snapshot
    if (form.sourceType !== 'snapshot') {
      if (!form.cpu.trim()) {
        newErrors.cpu = 'CPU is required';
      }

      if (!form.memory.trim()) {
        newErrors.memory = 'Memory is required';
      }
    }

    if (form.count < 1 || form.count > 1_000) {
      newErrors.count = 'Count must be between 1 and 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Include any uncommitted draft label from the labels editor
    const finalLabels = { ...form.labels };
    const draft = labelsEditorRef.current?.getDraft();
    if (draft) {
      finalLabels[draft.key] = draft.value;
    }

    const sandboxRequest: SandboxRequest = {
      labels: Object.keys(finalLabels).length > 0 ? finalLabels : undefined,
      // vmmType is only specified for disk images, not snapshots (snapshot has its own vmmType)
      vmmType: form.sourceType !== 'snapshot' ? form.vmmType : undefined,
      sourcesRef:
        form.sourceType === 'pod'
          ? {
              pod: {
                containers: form.podContainers.map((c) => ({
                  name: c.name.trim(),
                  diskImage:
                    c.diskImageType === 'public'
                      ? { name: c.publicDiskImageId, isPublic: true }
                      : { id: c.diskImageId, isPublic: false },
                })),
              },
            }
          : form.sourceType === 'diskImage'
            ? {
                diskImage: {
                  id: form.diskImageId,
                  isPublic: false,
                },
              }
            : form.sourceType === 'publicDiskImage'
              ? {
                  diskImage: {
                    name: form.publicDiskImageId,
                    isPublic: true,
                  },
                }
              : {
                  snapshot: {
                    id: form.snapshotId,
                  },
                },
      // Only include resources when not creating from a snapshot
      // (snapshots have fixed resources that cannot be overridden)
      resources:
        form.sourceType !== 'snapshot'
          ? {
              cpu: form.cpu.trim(),
              memory: form.memory.trim(),
              disk: form.disk.trim() || undefined,
            }
          : undefined,
    };

    const batchRequest: BatchSandboxRequest = {
      sandbox: sandboxRequest,
      count: form.count,
    };

    const startTime = performance.now();
    createBatchMutation.mutate(batchRequest, {
      onSuccess: (data) => {
        const endTime = performance.now();
        setTimeTakenMs(Math.round(endTime - startTime));
        setBatchResults(data);
        setShowResults(true);
      },
    });
  };

  const handleViewSandboxes = () => {
    navigate('/sandboxes');
  };

  const handleCreateAnother = () => {
    setShowResults(false);
    setBatchResults(null);
    setTimeTakenMs(0);
    setForm(initialFormState);
    setSelectedPublicImageGroup('');
  };

  // Separate private and public disk images
  const privateDiskImagesArray = privateDiskImages || [];
  const publicDiskImagesArray = useMemo(() => publicDiskImages || [], [publicDiskImages]);
  const availableSnapshots = snapshotsData || [];

  const privateDiskImageOptions = privateDiskImagesArray.map((img) => {
    const displayName = img.labels && img.labels['name'] ? img.labels['name'] : img.id;
    const baseImage = img.image?.base || 'Unknown';
    return {
      value: img.id,
      label: `${displayName} - ${baseImage}`,
    };
  });

  const publicDiskImageOptions = publicDiskImagesArray
    .filter((img) => img.name) // Filter out any images without a name
    .map((img) => ({
      value: img.name,
      label: img.name,
    }));

  // Group public disk images by base name (e.g., "python-3.11" -> group "python", version "3.11")
  const publicDiskImageGroups = useMemo(
    () => groupPublicDiskImages(publicDiskImagesArray),
    [publicDiskImagesArray]
  );

  const publicDiskImageGroupOptions = useMemo(
    () => groupsToSelectOptions(publicDiskImageGroups),
    [publicDiskImageGroups]
  );

  const snapshotOptions = availableSnapshots.map((snapshot) => {
    const createdAt = new Date(snapshot.createdAtUtc).toLocaleString();
    const sandboxInfo = snapshot.sandboxId ? ` (Sandbox: ${snapshot.sandboxId})` : '';
    return {
      value: snapshot.id,
      label: `${snapshot.id}${sandboxInfo} - ${createdAt}`,
    };
  });

  // If showing results, display the results page
  if (showResults && batchResults) {
    return (
      <div>
        <PageHeader
          title="Batch Creation Results"
          description="Summary of batch sandbox creation"
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: 'Sandboxes', href: '/sandboxes' },
            { label: 'Batch Create' },
          ]}
        />

        <div className="space-y-6">
          <Card>
            <CardHeader title="Summary" description="Batch creation results" />
            <CardBody>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-sm text-gray-500 dark:text-gray-400">Total Requested</div>
                  <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {batchResults.totalRequested}
                  </div>
                </div>
                <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <div className="text-sm text-green-600 dark:text-green-400">Succeeded</div>
                  <div className="text-2xl font-bold text-green-700 dark:text-green-300">
                    {batchResults.succeeded}
                  </div>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <div className="text-sm text-red-600 dark:text-red-400">Failed</div>
                  <div className="text-2xl font-bold text-red-700 dark:text-red-300">
                    {batchResults.failed}
                  </div>
                </div>
                <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="text-sm text-blue-600 dark:text-blue-400">Time Taken</div>
                  <div className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                    {timeTakenMs.toLocaleString()} ms
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {batchResults.sandboxes && batchResults.sandboxes.length > 0 && (
            <Card>
              <CardHeader
                title="Created Sandboxes"
                description={`${batchResults.sandboxes.length} sandboxes created successfully`}
              />
              <CardBody>
                <div className="space-y-2">
                  {batchResults.sandboxes.map((sandbox) => (
                    <div
                      key={sandbox.id}
                      className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          {sandbox.labels && Object.keys(sandbox.labels).length > 0 ? (
                            <div className="flex flex-wrap gap-1">
                              {Object.entries(sandbox.labels as Record<string, string>).map(
                                ([key, value]) => (
                                  <span
                                    key={key}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
                                  >
                                    {key}: {value}
                                  </span>
                                )
                              )}
                            </div>
                          ) : (
                            sandbox.id
                          )}
                        </div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          ID: {sandbox.id}
                        </div>
                      </div>
                      <Link
                        to={
                          sandbox.sandboxGroupId
                            ? `/sandbox-groups/${encodeURIComponent(sandbox.sandboxGroupId)}/sandboxes/${encodeURIComponent(sandbox.id)}`
                            : `/sandboxes/${encodeURIComponent(sandbox.id)}`
                        }
                      >
                        <Button variant="secondary" size="sm">
                          View Details
                        </Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          {batchResults.errors && batchResults.errors.length > 0 && (
            <Card>
              <CardHeader
                title="Errors"
                description={`${batchResults.errors.length} errors occurred during batch creation`}
              />
              <CardBody>
                <div className="space-y-2">
                  {batchResults.errors.map((error: string, index: number) => (
                    <div
                      key={index}
                      className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300"
                    >
                      {error}
                    </div>
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          <Card>
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Button variant="secondary" onClick={handleCreateAnother}>
                  Create Another Batch
                </Button>
                <Button onClick={handleViewSandboxes}>View All Sandboxes</Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </div>
    );
  }

  // Normal form view
  return (
    <div>
      <PageHeader
        title="Create Batch Sandboxes"
        description="Create multiple sandboxes from the same disk image or snapshot"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Sandboxes', href: '/sandboxes' },
          { label: 'Batch Create' },
        ]}
        action={
          <Link to="/sandboxes">
            <Button variant="secondary" leftIcon={<ArrowLeft className="h-4 w-4" />}>
              Cancel
            </Button>
          </Link>
        }
      />

      <form onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Batch Configuration */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Batch Configuration"
              description="Specify how many sandboxes to create"
            />
            <CardBody>
              <Input
                type="number"
                label="Number of Sandboxes"
                placeholder="5"
                value={form.count.toString()}
                onChange={(e) => handleChange('count', parseInt(e.target.value) || 1)}
                error={errors.count}
                helperText="Number of sandboxes to create (1-1000)"
                required
                min={1}
                max={1_000}
              />
            </CardBody>
          </Card>

          {/* Basic Information */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Basic Information"
              description="Provide basic details for all sandboxes (optional)"
            />
            <CardBody>
              <div className="space-y-4">
                <div>
                  <LabelsEditor
                    ref={labelsEditorRef}
                    value={form.labels}
                    onChange={(labels) => setForm((prev) => ({ ...prev, labels }))}
                    helperText="Add key-value pairs to label all sandboxes in this batch"
                  />
                </div>
                <Select
                  label="VMM Type"
                  options={[{ value: 'CloudHypervisor', label: 'Cloud Hypervisor' }]}
                  value={form.vmmType}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, vmmType: e.target.value as VmmType }))
                  }
                />
              </div>
            </CardBody>
          </Card>

          {/* Source Selection */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Source"
              description="Select the source type and choose from available options"
            />
            <CardBody>
              {/* Radio buttons for source type */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Source Type
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="diskImage"
                      checked={form.sourceType === 'diskImage'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Disk Image
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="publicDiskImage"
                      checked={form.sourceType === 'publicDiskImage'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">
                      Public Disk Image
                    </span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="snapshot"
                      checked={form.sourceType === 'snapshot'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Snapshot</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="sourceType"
                      value="pod"
                      checked={form.sourceType === 'pod'}
                      onChange={(e) => handleChange('sourceType', e.target.value as SourceType)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900 dark:text-gray-100">Pod</span>
                    <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                      preview
                    </span>
                  </label>
                </div>
              </div>

              {/* Conditional rendering based on source type */}
              {form.sourceType === 'diskImage' ? (
                <>
                  <Select
                    label="Disk Image"
                    options={privateDiskImageOptions}
                    value={form.diskImageId}
                    onChange={(e) => handleChange('diskImageId', e.target.value)}
                    error={errors.diskImageId}
                    placeholder={
                      diskImagesLoading ? 'Loading disk images...' : 'Select a disk image'
                    }
                    disabled={diskImagesLoading}
                  />
                  {privateDiskImageOptions.length === 0 && !diskImagesLoading && (
                    <p className="mt-2 text-sm text-warning-500">
                      No disk images available. Please create a disk image first.
                    </p>
                  )}
                </>
              ) : form.sourceType === 'publicDiskImage' ? (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Select
                      label="Public Disk Image"
                      options={publicDiskImageGroupOptions}
                      value={selectedPublicImageGroup}
                      onChange={(e) => {
                        const groupKey = e.target.value;
                        setSelectedPublicImageGroup(groupKey);
                        const group = publicDiskImageGroups[groupKey];
                        if (group && group.versions.length === 1) {
                          handleChange('publicDiskImageId', group.versions[0].value);
                        } else {
                          handleChange('publicDiskImageId', '');
                        }
                      }}
                      error={
                        errors.publicDiskImageId && !selectedPublicImageGroup
                          ? errors.publicDiskImageId
                          : undefined
                      }
                      placeholder={
                        publicDiskImagesLoading
                          ? 'Loading public disk images...'
                          : 'Select an image'
                      }
                      disabled={publicDiskImagesLoading}
                    />
                    {selectedPublicImageGroup &&
                      publicDiskImageGroups[selectedPublicImageGroup]?.versions.length > 1 && (
                        <Select
                          label="Version"
                          options={publicDiskImageGroups[selectedPublicImageGroup].versions}
                          value={form.publicDiskImageId}
                          onChange={(e) => handleChange('publicDiskImageId', e.target.value)}
                          error={
                            errors.publicDiskImageId && selectedPublicImageGroup
                              ? errors.publicDiskImageId
                              : undefined
                          }
                          placeholder="Select a version"
                        />
                      )}
                  </div>
                  {publicDiskImageGroupOptions.length === 0 && !publicDiskImagesLoading && (
                    <p className="mt-2 text-sm text-warning-500">
                      No public disk images available.
                    </p>
                  )}
                </>
              ) : form.sourceType === 'snapshot' ? (
                <>
                  <Select
                    label="Snapshot"
                    options={snapshotOptions}
                    value={form.snapshotId}
                    onChange={(e) => handleChange('snapshotId', e.target.value)}
                    error={errors.snapshotId}
                    placeholder={snapshotsLoading ? 'Loading snapshots...' : 'Select a snapshot'}
                    disabled={snapshotsLoading}
                  />
                  {availableSnapshots.length === 0 && !snapshotsLoading && (
                    <p className="mt-2 text-sm text-warning-500">No snapshots available.</p>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  {form.podContainers.map((c, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 dark:border-[#2a2a2a] rounded-lg p-3 space-y-2"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                          Container {idx + 1}
                        </span>
                        {form.podContainers.length > 1 && (
                          <button
                            type="button"
                            onClick={() =>
                              setForm((prev) => ({
                                ...prev,
                                podContainers: prev.podContainers.filter((_, i) => i !== idx),
                              }))
                            }
                            className="text-gray-400 hover:text-danger-500"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <Input
                        label="Name"
                        placeholder="e.g., webserver"
                        value={c.name}
                        onChange={(e) =>
                          setForm((prev) => ({
                            ...prev,
                            podContainers: prev.podContainers.map((pc, i) =>
                              i === idx ? { ...pc, name: e.target.value } : pc
                            ),
                          }))
                        }
                        required
                      />
                      <div className="flex gap-4 mb-1">
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`batch-container-${idx}-type`}
                            value="public"
                            checked={c.diskImageType === 'public'}
                            onChange={() =>
                              setForm((prev) => ({
                                ...prev,
                                podContainers: prev.podContainers.map((pc, i) =>
                                  i === idx ? { ...pc, diskImageType: 'public' as const } : pc
                                ),
                              }))
                            }
                            className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">
                            Public
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer">
                          <input
                            type="radio"
                            name={`batch-container-${idx}-type`}
                            value="private"
                            checked={c.diskImageType === 'private'}
                            onChange={() =>
                              setForm((prev) => ({
                                ...prev,
                                podContainers: prev.podContainers.map((pc, i) =>
                                  i === idx ? { ...pc, diskImageType: 'private' as const } : pc
                                ),
                              }))
                            }
                            className="h-3.5 w-3.5 text-primary-600 focus:ring-primary-500 border-gray-300"
                          />
                          <span className="ml-1.5 text-xs text-gray-700 dark:text-gray-300">
                            Private
                          </span>
                        </label>
                      </div>
                      {c.diskImageType === 'public' ? (
                        <Select
                          options={publicDiskImageOptions}
                          value={c.publicDiskImageId}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              podContainers: prev.podContainers.map((pc, i) =>
                                i === idx ? { ...pc, publicDiskImageId: e.target.value } : pc
                              ),
                            }))
                          }
                          placeholder="Select a public disk image"
                          disabled={publicDiskImagesLoading}
                        />
                      ) : (
                        <Select
                          options={privateDiskImageOptions}
                          value={c.diskImageId}
                          onChange={(e) =>
                            setForm((prev) => ({
                              ...prev,
                              podContainers: prev.podContainers.map((pc, i) =>
                                i === idx ? { ...pc, diskImageId: e.target.value } : pc
                              ),
                            }))
                          }
                          placeholder="Select a disk image"
                          disabled={diskImagesLoading}
                        />
                      )}
                    </div>
                  ))}
                  {form.podContainers.length < 10 && (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() =>
                        setForm((prev) => ({
                          ...prev,
                          podContainers: [
                            ...prev.podContainers,
                            {
                              name: '',
                              diskImageType: 'public',
                              diskImageId: '',
                              publicDiskImageId: '',
                            },
                          ],
                        }))
                      }
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add Container ({10 - form.podContainers.length} remaining)
                    </Button>
                  )}
                </div>
              )}
            </CardBody>
          </Card>

          {/* Resources */}
          <Card className="lg:col-span-2">
            <CardHeader
              title="Resources"
              description={
                form.sourceType === 'snapshot'
                  ? 'Resource configuration is defined by the snapshot and cannot be modified'
                  : 'Configure the resources for each sandbox'
              }
            />
            {form.sourceType !== 'snapshot' && (
              <CardBody>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Input
                    label="CPU"
                    placeholder="2000m"
                    value={form.cpu}
                    onChange={(e) => handleChange('cpu', e.target.value)}
                    error={errors.cpu}
                    helperText="CPU in millicores (e.g., 2000m = 2 cores)"
                    required
                  />
                  <Input
                    label="Memory"
                    placeholder="4Gi"
                    value={form.memory}
                    onChange={(e) => handleChange('memory', e.target.value)}
                    error={errors.memory}
                    helperText="Memory in Gi or Mi (e.g., 4Gi)"
                    required
                  />
                  <Input
                    label="Disk"
                    placeholder="10Gi"
                    value={form.disk}
                    onChange={(e) => handleChange('disk', e.target.value)}
                    helperText="Disk size in Gi (e.g., 10Gi)"
                  />
                </div>
              </CardBody>
            )}
            <CardFooter>
              <div className="flex justify-end gap-3">
                <Link to="/sandboxes">
                  <Button variant="secondary" type="button">
                    Cancel
                  </Button>
                </Link>
                <Button
                  type="submit"
                  leftIcon={<Save className="h-4 w-4" />}
                  isLoading={createBatchMutation.isPending}
                  disabled={
                    form.sourceType === 'diskImage'
                      ? privateDiskImageOptions.length === 0
                      : form.sourceType === 'publicDiskImage'
                        ? publicDiskImageOptions.length === 0
                        : form.sourceType === 'pod'
                          ? form.podContainers.length === 0
                          : availableSnapshots.length === 0
                  }
                >
                  Create {form.count} Sandboxes
                </Button>
              </div>
            </CardFooter>
          </Card>
        </div>
      </form>
    </div>
  );
}
